import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { Loan, LoanPagination, LoanStatus, LoanUser } from './loans.types';
import loansData from '../../../../assets/loans.json';
import { environment } from 'app/environment/environment';
import { User } from 'app/modules/user/users/users.types';
import { LoanDocument } from 'app/models/borrower/borrower-document.model';
import { BorrowerContact } from 'app/models/borrower/borrower-contact.model';
import { LoanInformation } from 'app/models/borrower/loan-information.model';
import { EmploymentDetail } from 'app/models/borrower/borrower-employment.model';
import { BorrowerExpenses } from 'app/models/borrower/borrower-expenses.model';
import { BorrowerCosigner } from 'app/models/borrower/borrower-cosigner.model';
import { CosignerDetail } from 'app/models/cosigner/cosigner-contact.model';
import { CosignerFinancial } from 'app/models/cosigner/cosigner-financial.model';
import { BydLoanDetail } from 'app/models/byd/byd-loan.model';
import { BydLoanNote } from 'app/models/byd/byd-note.model';
import { InternalLoanNote } from 'app/models/byd/internal-loan-note.model';
import { BorrowerRelation } from 'app/models/borrower/borrower-relation.model';
import { PayeeContact } from 'app/models/payee/payee-contact.model';
import { PayeeFinancial } from 'app/models/payee/payee-financial.model';

@Injectable({
    providedIn: 'root'
})
export class LoansService
{
    // Private
    private _pagination: BehaviorSubject<LoanPagination | null> = new BehaviorSubject(null);
    private _loans: BehaviorSubject<Loan[] | null> = new BehaviorSubject(null);
    private _selectedLoans: Loan[];
    private _rmUsers: BehaviorSubject<User[] | null> = new BehaviorSubject(null);
    private _loansDetails: BehaviorSubject<any> = new BehaviorSubject(null);
    private _loansDocument: BehaviorSubject<any> = new BehaviorSubject(null);
    private _loansAudit: BehaviorSubject<any> = new BehaviorSubject(null);
    private _borrowerAudit: BehaviorSubject<any> = new BehaviorSubject(null);
    private _returnUrl:string = ""

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient)
    {
    }

    currentFilter: string = '';

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for pagination
     */
    get pagination(): Observable<LoanPagination>
    {
        return this._pagination.asObservable();
    }

    /**
     * Getter for loans
     */
    get loans(): Observable<Loan[]>
    {
        return this._loans.asObservable();
    }

    /**
     * Getter & setter for selected loans
     */
    get selectedLoans(): Loan[]
    {
        return this._selectedLoans;
    }
    set selectedLoans(loans: Loan[])
    {
        this._selectedLoans = loans;
    }

    get returnUrl(){
        return this._returnUrl; 
    }
    set returnUrl(url){
        this._returnUrl = url;
    }

    /**
     * Getter for rm users
     */
    get rmUsers(): Observable<User[]>
    {
        return this._rmUsers.asObservable();
    }

    getLoans(userId: string):
        Observable<any>
    {
        /*return this._httpClient.get<{ loans: Loan[] }>('/assets/loans.json').pipe(
            tap((response) => {
                //this._pagination.next(response.pagination);
                this._loans.next(response.loans);
            })
        );*/

        return this._httpClient.get<{ loans: Loan[] }>(`${environment.API_BASE_PATH}api/loan/get/${userId}`, {
            params: {
                skip: 2,
                pageSize: 10
            }
        }).pipe(
            tap((response) => {
                //this._pagination.next(response.pagination);
                this._loans.next(response.data.loanList);
            })
        );
    }

    /**
     * Get RM users list
     *
     *
     * 
     */
    getRMUsers(): Observable<any>
    {
        return this._httpClient.get<{ users: User[] }>(`${environment.API_BASE_PATH}api/user`).pipe(
            tap((response) => {
                //this._pagination.next(response.pagination);
                this._rmUsers.next(response.data.userList.filter(
                    (user) => user.roles[0] == "Relationship Manager"
                ));
            })
        );
    }

    //API for Assign Loan to the RM user
    apiAssignLoanToUser(loanUserModel: LoanUser): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/user/linking`, loanUserModel);
    }
    
    //API for get loan details using loan number
    apiGetLoanDetails(loanNumber: string): Observable<any>
    {
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan-detail/get/${loanNumber}`).pipe(
            tap((response) => {
                //this._pagination.next(response.pagination);
                this._loansDetails.next(response.data);
            })
        );
    }

    //API for update loan status (legacy Internal-only write — prefer apiUpdateApplicationStage)
    apiUpdateLoanStatus(loanStatusModel: LoanStatus): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update/status`, loanStatusModel);
    }

    // Unified Application Stage — dual-writes status + borrowerStatus + both audits
    apiUpdateApplicationStage(loanStatusModel: LoanStatus): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update/application-stage`, loanStatusModel);
    }

    // Legacy JSON (closed/declined subcategories, leads). Prefer apiGetLoanStatusCatalog for status labels.
    apiGetStatusList(): Observable<any>
    {
        return this._httpClient.get('../../../../assets/loanStatusList.json');
    }

    // Single catalog (code + label + metadata)
    apiGetLoanStatusCatalog(): Observable<any>
    {
        return this._httpClient.get('../../../../assets/loanStatuses.catalog.json');
    }

    //API for get loan audit history
    apiGetLoanAudit(loanNumber: string): Observable<any>
    {
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/audit/${loanNumber}`).pipe(
            tap((response) => {
                //this._pagination.next(response.pagination);
                this._loansAudit.next(response.data);
            })
        );
    }

    //API for update borrower status
    apiUpdateBorrowerStatus(loanStatusModel: LoanStatus): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update/borrower-status`, loanStatusModel);
    }

    //API for get borrower audit history
    apiGetBorrowerAudit(loanNumber: string): Observable<any>
    {
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/audit/borrower/${loanNumber}`).pipe(
            tap((response) => {
                //this._pagination.next(response.pagination);
                this._borrowerAudit.next(response.data);
            })
        );
    }

    //API to download uploaded loan documents
    apiDownloadLoanDocument(loanDocument: LoanDocument): Observable<any>
    {
        return this._httpClient.get(`${environment.API_BASE_PATH}api/document/document/${loanDocument.documentId}`).pipe(
            tap((response) => {
                this._loansDocument.next(response);
            })
        );
    }

    //API for update Loan Information
    apiUpdateLoanInformation(loanInformationModel: LoanInformation): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateLoanInformation`, loanInformationModel);
    }

    //API for update Borrower Employment details
    apiUpdateBorrowerEmploymentDetails(borrowerEmploymentModel: EmploymentDetail): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerEmployement`, borrowerEmploymentModel);
    }

    //API for update Borrower Expenses details
    apiUpdateBorrowerExpensesDetails(borrowerExpenseModel: BorrowerExpenses): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerExpenses`, borrowerExpenseModel);
    }

    //API for update Borrower Cosigner details
    apiUpdateBorrowerCosignerDetails(borrowerCosignerModel: BorrowerExpenses): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerCosigner`, borrowerCosignerModel);
    }

    getUsersByRole(role): Observable<any>
    {
        return this._httpClient.get(`${environment.API_BASE_PATH}api/user/${role}`);
    }

    getLoanList(userId, pageIndex, pageSize, filter): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan-list/${userId}`,{
            pageIndex: pageIndex,
            pageSize: pageSize,
            filter: filter
        });
    }

    getLoanListHold(userId, pageIndex, pageSize, filter): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan-list-hold/${userId}`,{
            pageIndex: pageIndex,
            pageSize: pageSize,
            filter: filter
        });
    }

    getLoanStepCompleted(loanNumber): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/getStepCompleted/${loanNumber}`);
    }

    ////////////////////// Borrower Contact //////////////////////////////
    apiPostBorrowerContact(borrowerContactModel: BorrowerContact): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/borrower-contact`, borrowerContactModel);
    }

    //API for update Borrower Contact
    apiPutBorrowerContact(borrowerContactModel: BorrowerContact): Observable<any>
    {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerContact`, borrowerContactModel);
    }

    /////////////////// Loan Information /////////////////////////////////
    apiPostLoanInformation(loanInformationModel: LoanInformation): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/loan-information`, loanInformationModel);
    }

    apiPutLoanInformation(loanInformationModel: LoanInformation): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateLoanInformation`, loanInformationModel);
    }

    /////////////////// Employment /////////////////////////////////
    apiPostBorrowerEmployment(borrwerEmploymentModel: EmploymentDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/borrower-employement`, borrwerEmploymentModel);
    }

    apiPutBorrowerEmployment(borrwerEmploymentModel: EmploymentDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerEmployement`, borrwerEmploymentModel);
    }

    /////////////////// Borrower Expenses /////////////////////////////////
    apiPostBorrowerExpenses(borrwerExpenseModel: BorrowerExpenses): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/borrower-expenses`, borrwerExpenseModel);
    }

    apiPutBorrowerExpenses(borrwerExpenseModel: BorrowerExpenses): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerExpenses`, borrwerExpenseModel);
    }

    /////////////////// Borrower Cosigner /////////////////////////////////
    apiPostBorrowerCosigner(borrwerCosignerModel: BorrowerCosigner): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/borrower-cosigner`, borrwerCosignerModel);
    }

    apiPutBorrowerCosigner(borrwerCosignerModel: BorrowerCosigner): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateBorrowerCosigner`, borrwerCosignerModel);
    }

    /////////////////// Cosigner Contact /////////////////////////////////
    apiPostCosignerContact(cosignerDetailModel: CosignerDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/cosigner-detail`, cosignerDetailModel);
    }

    apiPutCosignerContact(cosignerDetailModel: CosignerDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateCosignerContact`, cosignerDetailModel);
    }

    /////////////////// Cosigner Expenses /////////////////////////////////
    apiPostCosignerFinancial(cosignerFinancialModel: CosignerFinancial): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/cosiger-expenses`, cosignerFinancialModel);
    }

    apiPutCosignerFinancial(cosignerFinancialModel: CosignerFinancial): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateCosigerExpenses`, cosignerFinancialModel);
    }

    /////////////////// Loan Documents /////////////////////////////////
    apiDownloadDocument(documentId, params: URLSearchParams = new URLSearchParams()): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/document/document/${documentId}`);
    }

    apiGetDocument(loanNumber, userType): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/upload/document/${loanNumber}/${userType}`);
    }

    apiPostDocument(formData, loanNumber, documentType, userType): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/upload/document/${loanNumber}/${userType}/${documentType}`, formData);
    }

    apiPreviewDocument(documentId, params: URLSearchParams = new URLSearchParams()): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/document/view/${documentId}`);
    }

    apiPostDocumentDelete(documentId): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/document/delete`, {
            documentId: documentId
        });
    }

    apiPostLoanByd(loanBydModel: BydLoanDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/loan-byd`, loanBydModel);
    }

    apiPutLoanByd(loanBydModel: BydLoanDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateLoanByd`, loanBydModel);
    }

    apiPostPushToLoanPro(loanNumber): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/pushToLoanPro`, {loanNumber:loanNumber});
    }

    apiPostLoanNote(loanNoteModel: BydLoanNote): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/loan-note`, loanNoteModel);
    }

    apiPostInternalLoanNote(internalNoteModel: InternalLoanNote): Observable<any> {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/internal-note`, internalNoteModel);
    }

    apiUpdateInternalLoanNote(noteId: any, internalNoteModel: any): Observable<any> {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update-internal-note/${noteId}`, internalNoteModel);
    }

    apiPutLoanNote(loanNoteModel: BydLoanNote): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/updateLoanNote`, loanNoteModel);
    }


    apiPostLoanDelete(loanNumber): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/delete`, {
            loanNumber: loanNumber
        });
    }

    getActiveLoanList(userId, pageIndex, pageSize, filter): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/active/loan-list/${userId}`,{
            pageIndex: pageIndex,
            pageSize: pageSize,
            filter: filter
        });
    }

    getClosedLoanList(userId, pageIndex, pageSize, filter): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/closed/loan-list/${userId}`,{
            pageIndex: pageIndex,
            pageSize: pageSize,
            filter: filter
        });
    }

//////////////////////////////// Payee ////////////////////////////////////////////
    apiPostBorrowerPayee(borrwerPayeeModel: BorrowerRelation): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/borrower-payee/save`, borrwerPayeeModel);
    }

    apiPutBorrowerPayee(borrwerPayeeModel: BorrowerRelation): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/borrower-payee/update`, borrwerPayeeModel);
    }

    apiGetBorrowerPayee(loanNumber: string): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/borrower-payee/get/${loanNumber}`);
    }

//////////////////////////////// Payee Contact ////////////////////////////////////////////
    apiGetPayeeContact(loanNumber: string): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/payee-contact/get/${loanNumber}`);
    }

    apiPostPayeeContact(payeeModel: PayeeContact): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/payee-contact/save`, payeeModel);
    }

    apiPutPayeeContact(payeeModel: PayeeContact): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/payee-contact/update`, payeeModel);
    }

//////////////////////////////// Payee Contact ////////////////////////////////////////////
    apiGetPayeeEmployment(loanNumber: string): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/payee-employment/get/${loanNumber}`);
    }

    apiPostPayeeEmployment(payeeModel: EmploymentDetail): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/payee-employment/save`, payeeModel);
    }

//////////////////////////////// Payee Financial ////////////////////////////////////////////
    apiGetPayeeFinancial(loanNumber: string): Observable<any>{
        return this._httpClient.get(`${environment.API_BASE_PATH}api/loan/payee-expense/get/${loanNumber}`);
    }

    apiPostPayeeFinancial(payeeModel: PayeeFinancial): Observable<any>{
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/payee-expense/save`, payeeModel);
    }

    // API call update loan note
    apiUpdateLoanNote(noteId: any, loanNoteModel: any): Observable<any> {
    return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update-note/${noteId}`, loanNoteModel);
}

    // API call to update note on borrower audit Status
    apiUpdateBorrowerAuditNote(noteId: any, noteModel: any): Observable<any> {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update-borrower-audit-note/${noteId}`, noteModel);
    }

    // API call to update note on loan audit Status
    apiUpdateLoanAuditNote(noteId: any, noteModel: any): Observable<any> {
        return this._httpClient.post(`${environment.API_BASE_PATH}api/loan/update-loan-audit-note/${noteId}`, noteModel);
    }
}